import { t } from '@lingui/macro'
import {
    EmissionFactorWithGasEmissions,
    GasEmissions,
    TransactionDocumentEmissionEstimateFallback,
} from '@lune-climate/lune'
import { Text, TooltipList } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import Big from 'big.js'
import moment from 'moment'
import { FC, useMemo } from 'react'

import { useLuneI18nLocale } from '../../hooks'
import { formatToCurrency, getCountryNameByThreeLetterCode } from '../../utils/utils'

import EmissionFactorExplanationCard from './EmissionFactorExplanationCard'
import ExplanationChip from './ExplanationChip'
import ExplanationSteps, { ExplanationStep, ExplanationText } from './ExplanationSteps'

interface TransactionDocumentEmissionsExplanationFallbackSectionProps {
    fallback: TransactionDocumentEmissionEstimateFallback
    enableEmissionFactorLink: boolean
}

export const TransactionDocumentEmissionsExplanationFallbackSection: FC<
    TransactionDocumentEmissionsExplanationFallbackSectionProps
> = ({ fallback, enableEmissionFactorLink }) => {
    const locale = useLuneI18nLocale()

    const {
        countryCode,
        category,
        emissionFactor,
        value,
        dietFactor,
        exchangeRateDate,
        exchangeRate,
    } = fallback

    const fallbackStep: JSX.Element = (
        <ExplanationText
            text={t`Failed to calculate line item emissions, using document-level emissions calculation instead`}
        />
    )

    const categoryStep = useMemo((): JSX.Element | undefined => {
        if (!category) {
            return undefined
        }
        return (
            <>
                <ExplanationText text={t`Extracted the`} />
                <ExplanationChip text={category} />
                <ExplanationText text={t`category based on your input`} />
            </>
        )
    }, [category, locale])

    const genericTextExtractionInfo: JSX.Element = (
        <>
            <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                {t`Extracted using AI:`}
            </Text>
            <TooltipList items={[t`Model: Llama 3.1:8b`]} />
        </>
    )

    const amountStep = useMemo((): JSX.Element => {
        const amountInInputCurrency = formatToCurrency(value.value, value.currency)
        return (
            <>
                <ExplanationText text={t`Extracted the amount of`} />
                <ExplanationChip text={amountInInputCurrency} />
                <ExplanationText text={t`based on your input`} />
            </>
        )
    }, [value, locale])

    const countryStep = useMemo((): JSX.Element => {
        return (
            <>
                <ExplanationText text={t`Selected the`} />
                <ExplanationChip
                    text={getCountryNameByThreeLetterCode(countryCode) ?? countryCode}
                />
                <ExplanationText text={t`country based on your input`} />
            </>
        )
    }, [countryCode, locale])

    const countryTextExtractionInfo: JSX.Element = (
        <>
            <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                {t`We matched your input where, to Lune's country:`}
            </Text>
            <TooltipList items={[t`Model: Llama 3.1:8b`]} />
        </>
    )

    const emissionFactorStep = useMemo((): JSX.Element => {
        return (
            <>
                <ExplanationText text={t`Selected the`} />
                <ExplanationChip text={emissionFactor.name} />
                <ExplanationText text={t`Emission Factor`} />
            </>
        )
    }, [emissionFactor, locale])

    const emissionFactorInfo: JSX.Element = (
        <>
            <Text variant={'caption'} component={'p'} sx={{ color: 'white' }}>
                {t`Based on category`}
            </Text>
            <TooltipList items={[t`Model: GTE-small`]} />
        </>
    )

    const exchangeRateStep = useMemo((): JSX.Element | undefined => {
        if (!exchangeRate || !exchangeRateDate) {
            return undefined
        }

        const amountInInputCurrency = formatToCurrency(
            fallback.value.value,
            fallback.value.currency,
        )
        const amountInConvertedCurrency = formatToCurrency(
            Big(fallback.value.value).mul(exchangeRate).toString(),
            emissionFactor.denominatorUnit,
        )
        const formattedDate = moment(exchangeRateDate).format('D MMMM YYYY')

        return (
            <>
                <ExplanationText text={t`Converted`} />
                <ExplanationChip text={amountInInputCurrency} />
                <ExplanationText text={t`to`} />
                <ExplanationChip text={amountInConvertedCurrency} />
                <ExplanationText text={t`based on the`} />
                <ExplanationChip text={formattedDate} />
                <ExplanationText text={t`exchange rate of`} />
                <ExplanationChip text={exchangeRate} />
            </>
        )
    }, [exchangeRate, exchangeRateDate, emissionFactor, value, locale])

    const multiplicationStep = useMemo((): JSX.Element => {
        return dietFactor ? (
            <ExplanationText text={t`Multiplied the adjusted Emission Factor by the amount`} />
        ) : (
            <ExplanationText text={t`Multiplied the Emission Factor by the amount`} />
        )
    }, [dietFactor, locale])

    const steps: ExplanationStep[] = [
        { description: fallbackStep },
        { description: categoryStep, info: genericTextExtractionInfo },
        { description: amountStep, info: genericTextExtractionInfo },
        { description: countryStep, info: countryTextExtractionInfo },
        { description: emissionFactorStep, info: emissionFactorInfo },
        ...(exchangeRateStep ? [{ description: exchangeRateStep }] : []),
        { description: multiplicationStep },
    ]

    return (
        <>
            <Box>
                <Text variant={'h5'} sx={{ mb: 4 }}>
                    {t`Methodology`}
                </Text>
                <ExplanationSteps steps={steps} />
            </Box>
            <Box>
                <EmissionFactorExplanationCard
                    emissionFactor={
                        emissionFactor as Omit<EmissionFactorWithGasEmissions, 'gasEmissions'> & {
                            gasEmissions: GasEmissions
                        }
                    }
                    amount={null}
                    label={emissionFactor.name}
                    {...(enableEmissionFactorLink
                        ? {
                              href: `/emission-factors/${emissionFactor.id}`,
                          }
                        : {})}
                />
            </Box>
        </>
    )
}
